import {useState} from 'react';

import {assert} from 'Asserts';
import {assertNever, ElementDragEvent} from 'util/Types';

export enum DragAndDropSystem {
  NONE = 'NONE',
  IN_GROUP_ONLY = 'IN_GROUP_ONLY',
  FREE = 'FREE',
}

export interface DragAndDropSidebarParams {
  /**
   * We allow either "free" or "in group" drag systems. Free means items can be dropped into
   * another group.
   */
  system: DragAndDropSystem;
  /**
   * This lets us track group-bounded drag operations. Note that this is the list of DROP targets.
   *    { 'PRE-RUN': [cid1, cid2], 'RUN': [cid3] ... }
   */
  droppableCidsByGroup: Record<string, Array<string>>;
  /**
   * List of any item that can be dragged. Note that this is not just a flattened
   * version of `droppableCidsByGroup`, as not every drop target is also draggable.
   */
  allDraggableCids: Array<string>;
  /**
   * Callback invoked when a D&D operation is finished.
   * @param draggedItem The CID of the item which was dragged
   * @param droppedOn The CID of the drop target
   */
  onDrop: (draggedItem: string, droppedOn: string) => void;
}

export interface DragAndDropSidebarOutputs {
  makeDragPropsForItemByCid: (cid: string) => DragAndDropStepItemProperties;
}

// Passed down to any draggable or droppable item in the sidebar
export interface DragAndDropStepItemProperties {
  // These attributes are made available for adjusting styles
  draggable: boolean;
  isBeingDragged: boolean;
  isBeingDraggedOver: boolean;

  // These event handlers should be installed into any draggable or droppable item. You can use a spread
  // to install them all; our code here will only respond to meaningful interactions.
  eventHandlers: {
    onDragStart?: (e: ElementDragEvent) => void;
    onDragOver?: (e: ElementDragEvent) => void;
    onDragLeave?: () => void;
    onDragEnd?: () => void;
    onDrop?: (e: ElementDragEvent) => void;
  };
}

/**
 * Manages drag and drop within the step selector sidebar in procedures. This hook does no
 * rendering or data modification. It manages state (which element is being dragged? etc),
 * and builds out the event handlers, which can be easily installed onto the items in the list.
 *
 * We expect the sidebar to be structured in a specific way:
 *   - They are grouped. Each group can be uniquely identified.
 *   - All elements in the group are DROP targets
 *   - Not all elements in the group can be dragged! (E.g., the Add Step button)
 *
 * We also allow the caller to limit the drag operation to either FREE or IN_GROUP. Using FREE
 * allows the user to drag an element into another group.
 *
 * @param params
 */
export default function useDragDropSidebar(params: DragAndDropSidebarParams): DragAndDropSidebarOutputs {
  const [currentDragItemCid, setCurrentDragItemCid] = useState<string | null>(null);
  const [currentDragOverItemCid, setCurrentDragOverItemCid] = useState<string | null>(null);
  const [viableDropTargetCids, setViableDropTargetCids] = useState<Array<string>>([]);

  // When the drag operation begins, we don't actually do much, other than set some local state.
  const onStartDrag = (cid: string): void => {
    setCurrentDragItemCid(cid);
    const cidsLists = Object.values(params.droppableCidsByGroup);

    switch (params.system) {
      case DragAndDropSystem.NONE:
        setViableDropTargetCids([]);
        break;

      case DragAndDropSystem.FREE:
        setViableDropTargetCids(cidsLists.flat());
        break;

      case DragAndDropSystem.IN_GROUP_ONLY:
        setViableDropTargetCids(cidsLists.find((itemCids) => itemCids.includes(cid)) ?? []);
        break;

      default:
        assert(false, `Unhandled drag & drop system:  ${assertNever(params.system)}`);
    }
  };

  const cleanup = (): void => {
    setCurrentDragItemCid(null);
    setViableDropTargetCids([]);
    setCurrentDragOverItemCid(null);
  };

  const makeDragPropsForItemByCid = (cid: string): DragAndDropStepItemProperties => {
    const blockAllOperations = params.system === DragAndDropSystem.NONE;
    const draggable = !blockAllOperations && params.allDraggableCids.includes(cid);
    const droppable = viableDropTargetCids.includes(cid);
    const isDragInProcess = currentDragItemCid != null;

    return {
      draggable,
      isBeingDragged: cid === currentDragItemCid,
      isBeingDraggedOver: cid === currentDragOverItemCid,

      eventHandlers: {
        onDragStart:
          draggable && !isDragInProcess && !blockAllOperations
            ? (e: ElementDragEvent) => {
                onStartDrag(cid);
                e.dataTransfer.dropEffect = 'move';
                return false;
              }
            : undefined,

        onDragOver:
          droppable && isDragInProcess && !blockAllOperations
            ? (e: ElementDragEvent) => {
                setCurrentDragOverItemCid(cid);
                e.dataTransfer.dropEffect = 'move';
                e.preventDefault();
                return false;
              }
            : undefined,

        onDragLeave:
          draggable && isDragInProcess && !blockAllOperations
            ? () => {
                setCurrentDragOverItemCid(null);
              }
            : undefined,

        onDragEnd: draggable && isDragInProcess && !blockAllOperations ? cleanup : undefined,

        onDrop:
          droppable && isDragInProcess && !blockAllOperations
            ? (e) => {
                e.stopPropagation();
                if (currentDragItemCid !== cid && viableDropTargetCids.includes(cid)) {
                  params.onDrop(currentDragItemCid, cid);
                }
                cleanup();
                return false;
              }
            : undefined,
      },
    };
  };

  return {
    makeDragPropsForItemByCid,
  };
}
